import Alpine from 'alpinejs';

document.addEventListener('alpine:init', () => {
    Alpine.data('carousel', (options = {}) => ({
        splide: null,
        loop: options.loop ?? true,
        scaleCenter: options.scaleCenter ?? true,
        firstSlideScale: options.firstSlideScale ?? 1,
        poll: options.poll ?? false,
        pollUrl: options.pollUrl ?? null,
        pollIds: options.pollIds ?? [],
        pollInterval: options.pollInterval ?? 60000,
        pollTimer: null,
        isPolling: false,

        init() {
            // This component lives inside an x-cloak wrapper, which is still
            // display:none while Alpine walks the tree and runs child
            // init() hooks. Mounting Splide against a hidden/zero-width
            // container gives it wrong measurements, so with focus:'center'
            // the featured slide lands off-center on load and the next
            // click "corrects" with a big, jarring jump instead of moving
            // one slide. Deferring to $nextTick waits until x-cloak is
            // removed and the container has real layout.
            this.$nextTick(() => {
                this.splide = new Splide(this.$refs.splide_carousel, {
                    type: this.loop ? 'loop' : 'slide',
                    // scaleCenter mode grows whichever slide is centered (see the
                    // .is-active CSS below) so it needs to actually sit in the
                    // middle of the track; scaleCenter:false only ever grows the
                    // first slide, so plain start-alignment reads more naturally.
                    focus: this.scaleCenter ? 'center' : 0,
                    perPage: 7,
                    gap: '30px',
                    // Splide's default (400ms, ease-out-quart) is CSS-transition
                    // linear-ish at the start; Embla's default feel comes from a
                    // friction/velocity physics sim (duration:25, friction:0.68)
                    // that snaps away fast and decelerates hard into place. This
                    // easing approximates that decay curve, with speed nudged up
                    // slightly so the deceleration reads as smooth rather than cut off.
                    speed: 650,
                    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                    breakpoints: {
                        991: {perPage: 5},
                        767: {perPage: 3},
                    },
                }).mount();

                this.fixMissingImageStatuettes();
                this.splide.on('resize', () => this.fixMissingImageStatuettes());

                if (this.poll && this.pollUrl) {
                    this.pollTimer = setInterval(() => this.pollForNewSlides(), this.pollInterval);

                    window.addEventListener('beforeunload', () => {
                        if (this.pollTimer) clearInterval(this.pollTimer);
                    });
                }
            });
        },

        pollForNewSlides() {
            if (this.isPolling) return;
            this.isPolling = true;

            const url = new URL(this.pollUrl, window.location.origin);
            url.searchParams.set('ids', this.pollIds.join(','));

            fetch(url, {headers: {Accept: 'text/html'}})
                .then(response => (response.status === 204 ? null : response.text()))
                .then(html => {
                    if (!html) return;

                    const list = this.$refs.splide_list;
                    const template = document.createElement('template');
                    template.innerHTML = html.trim();
                    const newSlides = Array.from(template.content.children);

                    if (newSlides.length === 0) return;

                    // Remember where the viewer currently is so inserting slides
                    // at the front doesn't shift what they're looking at
                    const previousIndex = this.splide.index;

                    // Newest entries go first, oldest fall off the end
                    newSlides.reverse().forEach(slide => list.prepend(slide));

                    this.pollIds = newSlides
                        .map(slide => slide.getAttribute('data-winner-id'))
                        .concat(this.pollIds);

                    this.splide.refresh();
                    this.fixMissingImageStatuettes();

                    // Every existing slide shifted forward by however many were
                    // just prepended - jump (no animation) back to the same slide
                    const maxIndex = list.children.length - 1;
                    this.splide.Components.Move.jump(Math.min(previousIndex + newSlides.length, maxIndex));
                })
                .catch(error => console.error('Error polling for new carousel slides:', error))
                .finally(() => {
                    this.isPolling = false
                });
        },

        // Nominations without a show image display a text label instead,
        // which is taller than a normal thumbnail - nudge the winner
        // statuette icon down to match so it doesn't sit too high.
        fixMissingImageStatuettes() {
            this.splide.Components.Elements.slides.forEach((slide) => {
                if (!slide.querySelector('.no-image-available')) return;

                const label = slide.querySelector('.nomination-image-missing-show-name');
                const statuette = slide.querySelector('.winner-statuette');
                if (!label || !statuette) return;

                statuette.style.setProperty('bottom', `${label.clientHeight - 15}px`, 'important');
            });
        },
    }));
});
